import { XMarkIcon } from '@heroicons/react/24/solid'
import { useToken, formatDateDot } from '../../../../utils'
import React, { useState, useCallback, useMemo } from 'react'
import { useEffect, useRef } from 'react'
import styles from './OfferModal.module.scss'
import { ChevronRightIcon } from '@heroicons/react/24/solid'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { useNavigate } from 'react-router-dom'
import { useCreateContractFromOffer } from '../../../../hooks/contract'
import { formatPrizeTypeLabel, INCENTIVE_BASIS_LABEL } from '../../../../utils/incentiveDisplay'

const OfferModal = React.memo(({ isOpen, onClose, data }) => {
	// const [offerId, setOfferId] = useState(null)

	const [domesticOverseasSwapContent, setDomesticOverseasSwapContent] = useState(true)
	const [firstLevelAgreeChecked, setFirstLevelAgreeChecked] = useState(false)
	const [secondLevelAgreeChecked, setSecondLevelAgreeChecked] = useState(false)

	const [nextButtonActivated, setNextButtonActivated] = useState(false)
	const navigate = useNavigate()
	const token = useToken()
	const createContractMutation = useCreateContractFromOffer()

	// console.log(authStatus)

	useEffect(() => {
		if (firstLevelAgreeChecked && secondLevelAgreeChecked) {
			setNextButtonActivated(true)
		} else {
			setNextButtonActivated(false)
		}
	}, [firstLevelAgreeChecked, secondLevelAgreeChecked])

	const modalRef = useRef(null)

	const sortIncentiveSettings = useCallback((incentiveSettings) => {
		if (!incentiveSettings || !Array.isArray(incentiveSettings)) {
			return []
		}

		const sorted = incentiveSettings.sort((a, b) => {
			// startRank가 null이나 undefined인 경우 처리
			const aRank = a.startRank || 999
			const bRank = b.startRank || 999
			return aRank - bRank
		})

		return sorted
	}, [])

	const handleClickOutside = useCallback(
		(event) => {
			// 미리보기 버튼 클릭은 무시
			if (
				(event.target.closest('button') &&
					event.target.closest('button').textContent === '미리보기') ||
				(event.target.closest('button') &&
					event.target.closest('button').textContent === '계약서 보기') ||
				event.target.closest('svg') ||
				(event.target.closest('button') &&
					event.target.closest('button').textContent === '계약 제안하기') ||
				(event.target.closest('button') &&
					event.target.closest('button').textContent === '닫기')
			) {
				return
			}

			if (modalRef.current && !modalRef.current.contains(event.target)) {
				onClose()
			}
		},
		[onClose],
	)

	useEffect(() => {
		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isOpen, handleClickOutside])

	// 천원단위 포맷팅 함수
	const formatNumber = useCallback((num) => {
		if (!num) return '0'
		return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
	}, [])

	useEffect(() => {
		console.log('data : ', data)
	}, [data])

	// 날짜에서 년도 앞 두 글자 제거 함수
	const formatYear = useCallback((dateString) => {
		if (!dateString) return ''
		// YYYY-MM-DD 형식에서 YYYY를 YY로 변환 (앞 두 글자 제거)
		return dateString.replace(/^\d{2}(\d{2}-)/, '$1')
	}, [])

	// 투어 카테고리 계산을 useMemo로 최적화 - user_type이 tourPro인 사람의 tour_type 사용
	const tourCategories = useMemo(() => {
		if (!data) return null

		// user와 targetUser 중에서 user_type이 tourPro인 사람 찾기
		const proUser =
			data.user?.user_type === 'tourPro'
				? data.user
				: data.targetUser?.user_type === 'tourPro'
					? data.targetUser
					: null

		return proUser?.user_tour_pro?.tour_type || null
	}, [data])

	// 계약 기간 계산을 useMemo로 최적화
	const contractPeriods = useMemo(() => {
		if (!data?.Offer_tour?.length) return { startDate: null, endDate: null }

		const earliestStartDate = data.Offer_tour.reduce((earliest, tour) => {
			return tour.tour.started_at < earliest ? tour.tour.started_at : earliest
		}, data.Offer_tour[0].tour.started_at)

		const latestEndDate = data.Offer_tour.reduce((latest, tour) => {
			return tour.tour.ended_at > latest ? tour.tour.ended_at : latest
		}, data.Offer_tour[0].tour.ended_at)

		return { startDate: earliestStartDate, endDate: latestEndDate }
	}, [data?.Offer_tour])

	const handleOffer = async () => {
		if (!token || !data?.id) {
			console.error('토큰 또는 오퍼 ID가 없습니다.')
			return
		}

		try {
			await createContractMutation.mutateAsync({
				token,
				offerId: data.id,
			})
			onClose(true)
		} catch (error) {
			console.error('계약 생성 실패:', error)
			onClose(false)
		}
	}

	const sortTourData = useCallback((tourData) => {
		if (!tourData || !Array.isArray(tourData)) {
			return []
		}

		const sorted = tourData.sort((a, b) => {
			// tour.tour.started_at 기준으로 정렬 (가장 이른 날짜가 앞에)
			const aStartDate = new Date(a.tour.started_at)
			const bStartDate = new Date(b.tour.started_at)
			return aStartDate - bStartDate
		})

		return sorted
	}, [])

	const dataForRender = useMemo(() => {
		if (!data) return null

		// 투어 데이터 정렬 (시작일 기준)
		const sortedTourData = sortTourData(data?.Offer_tour)

		const rawContractType = (
			data?.offer?.contract_type ||
			data?.offer?.offer_type ||
			data?.offer?.offerType ||
			data?.offer_type ||
			data?.offerType ||
			data?.contract_type ||
			data?.contractType ||
			''
		).toString()
		const termTypeValue = data?.term_type || data?.termContract?.termType || ''

		const normalizedContractType = (() => {
			const lowerType = rawContractType.toLowerCase().replace(/_/g, '-')
			if (lowerType.includes('short') || lowerType.includes('single')) return 'short'
			if (lowerType.includes('long')) return 'long'
			if (
				lowerType.includes('period') ||
				lowerType.includes('year') ||
				lowerType.includes('both') ||
				lowerType.includes('term')
			)
				return 'period'
			if (data?.termContract) return 'period'
			if (sortedTourData?.length === 1) return 'short'
			if (sortedTourData && sortedTourData.length > 1) return 'long'
			return 'period'
		})()

		const contractTypeLabel =
			normalizedContractType === 'short'
				? '단기계약'
				: normalizedContractType === 'long'
					? '장기계약'
					: '기간계약'

		const contractPeriodLabel = (() => {
			if (data?.termContract) {
				const lowerTerm = termTypeValue.toLowerCase()
				if (['full_year', 'full-year', 'both'].includes(lowerTerm)) return '상반기 / 하반기'
				if (['year_first_half', 'first_half', 'first-half'].includes(lowerTerm))
					return '상반기'
				if (['year_second_half', 'second_half', 'second-half'].includes(lowerTerm))
					return '하반기'
				return ''
			}

			if (sortedTourData?.length > 0) {
				const firstTour = sortedTourData[0]
				const lastTour = sortedTourData[sortedTourData.length - 1]
				const startRaw = firstTour?.tour?.started_at || firstTour?.started_at
				const endRaw = lastTour?.tour?.ended_at || lastTour?.ended_at
				const formattedStart = startRaw ? formatDateDot(startRaw) : ''
				const formattedEnd = endRaw ? formatDateDot(endRaw) : ''
				const startDate = formattedStart === '-' ? '' : formattedStart
				const endDate = formattedEnd === '-' ? '' : formattedEnd
				if (startDate && endDate) {
					return `${startDate} ~ ${endDate}`
				}
				return startDate || endDate || ''
			}

			return ''
		})()

		const renderData = {
			jobPostContractType: normalizedContractType,
			contractTypeLabel,
			isOverseas: data?.overseasContract !== null || data?.termContract?.isOverseasIncluded,
			isDomestic: data?.domesticContract !== null,
			domesticFormData: data?.domesticContract,
			overseasFormData: data?.overseasContract,
			selectedToursData: sortedTourData,
			isGeneralContract: data?.termContract !== null,
			generalFormData: data?.termContract,
			tourCategory: tourCategories,
			contractPeriods: contractPeriods,
			termType: termTypeValue,
			contractPeriodLabel,
		}

		console.log('dataForRender:', renderData)

		return renderData
	}, [data, sortTourData, tourCategories, contractPeriods])

	if (!isOpen) return null

	return (
		<div
			className={styles.previewModalContainer}
			ref={modalRef}
			onClick={(e) => e.stopPropagation()}>
			<div className={styles.scrollableContent}>
				<div className={styles.title}>계약 제안하기</div>
				<div className={styles.isOfferCorrect}>
					<span>
						<span className={styles.greenName}>{`'${data.user.name}' `}</span>
						<span>님에게</span>
					</span>
					<span>계약을 제안하시겠어요?</span>
				</div>
				<div className={styles.checkPlease}>
					<div>
						계약을 제안하면 전자 계약서가 이메일/카카오톡을 <br />
						통해 양측에 전달되고 서명단계로 넘어가게 됩니다
					</div>
				</div>
				{/* <div className={styles.viewContract}>계약서 보기</div> */}
				<div className={styles.basicContractInfoHeader}>기본 계약정보</div>
				<div className={styles.basicContractInfoTourType}>
					<div className={styles.basicLabel}>투어종류</div>
					<div className={styles.basicContent}>
						{dataForRender?.tourCategory || '미정'}
					</div>
				</div>
				<div className={styles.basicContractInfoContractType}>
					<div className={styles.basicLabel}>계약유형</div>
					<div className={styles.basicContent}>{dataForRender.contractTypeLabel}</div>
				</div>
				<div className={styles.periodContainer}>
					<div className={styles.periodLabel}>계약기간</div>
					<div className={styles.periodContent}>{dataForRender.contractPeriodLabel}</div>
				</div>
				<div className={styles.tourListContainer}>
					<div className={styles.tourListLabel}>대회목록</div>
					<div className={styles.tourListContent}>
						{(!dataForRender?.selectedToursData ||
							dataForRender.selectedToursData.length === 0) &&
							'미정'}
						{dataForRender?.selectedToursData?.length > 0 &&
							dataForRender.selectedToursData.map((tour, index) => (
								<div key={index} className={styles.tourListItemContainer}>
									<div className={styles.tourListItemIndex}>({index + 1})</div>
									<div className={styles.tourListItemMain}>
										<div className={styles.tourName}>{tour.tour.name}</div>
										<div className={styles.tourDate}>
											{formatYear(tour.tour.started_at).slice(0, 8)} ~{' '}
											{formatYear(tour.tour.ended_at).slice(0, 8)}
										</div>
									</div>
								</div>
							))}
					</div>
				</div>
				<div className={styles.isOverSeasTourIncludeContainer}>
					<div className={styles.isOverSeasTourIncludeLabel}>해외 대회 포함 여부</div>
					<div className={styles.isOverSeasTourIncludeContent}>
						{dataForRender?.isOverseas ? '해외대회 포함' : '해외대회 미포함'}
					</div>
				</div>
				<div className={styles.detailContractHeader}>상세 계약정보</div>
				{dataForRender?.isGeneralContract && (
					<>
						<div className={styles.applyTaxContainer}>
							<div className={styles.applyTaxLabel}>세금 적용 여부</div>
							<div className={styles.applyTaxContent}>
								{dataForRender?.generalFormData?.isSalaryTaxIncluded
									? '세후'
									: '세전'}
							</div>
						</div>
						<div className={styles.payPeriodContainer}>
							<div className={styles.payPeriodLabel}>임금 지급 주기</div>
							<div className={styles.payPeriodContent}>
								{dataForRender?.generalFormData?.payrollCycles === 'weekly'
									? '주급'
									: '일시불제'}
							</div>
						</div>
						<div className={styles.periodSalaryContainer}>
							<div className={styles.periodSalaryLabel}>기간 급여액</div>
							<div className={styles.periodSalaryContent}>
								{formatNumber(
									dataForRender?.generalFormData?.salaryAmountForPeriod,
								)}{' '}
								원
							</div>
						</div>
						<div className={styles.incentiveOptionContainer}>
							<div className={styles.incentiveOptionLabel}>
								{INCENTIVE_BASIS_LABEL}
							</div>
							<div className={styles.incentiveOptionContent}>
								{formatPrizeTypeLabel(dataForRender?.generalFormData?.prizeType)}
							</div>
						</div>
						<div className={styles.incentiveSettingsContainer}>
							<div className={styles.incentiveSettingsLabel}>인센티브 금액</div>
							<div className={styles.incentiveSettingsContent}>
								{sortIncentiveSettings(
									dataForRender?.generalFormData?.incentives,
								).map((setting) => {
									if (setting.percentage === null && setting.end === null)
										return null
									const displayRank =
										setting.endRank === 1
											? '우승 : '
											: setting.startRank +
												(setting.endRank !== null
													? setting.endRank === 0
														? ' ~ CUT :  '
														: ` ~ ${setting.endRank} :  `
													: '~ ')
									const displayPercentage =
										setting.percentage !== null
											? `  ${setting.percentage}%`
											: '미입력'
									return (
										<div
											key={setting.id}
											className={styles.incentiveDisplayItem}>
											<div>{displayRank}</div>
											<div>{displayPercentage}</div>
										</div>
									)
								})}
							</div>
						</div>
						<div className={styles.wearSponserHatContainer}>
							<div className={styles.wearSponserHatLabel}>스폰서 모자 착용 여부</div>
							<div className={styles.wearSponserHatContent}>
								{dataForRender?.generalFormData?.wearSponsoredHat
									? '착용'
									: '미착용'}
							</div>
						</div>
					</>
				)}
				{dataForRender?.isDomestic &&
					dataForRender?.isOverseas &&
					!dataForRender?.isGeneralContract &&
					dataForRender?.domesticFormData &&
					dataForRender?.overseasFormData && (
						<>
							<div className={styles.domesticOverseasSwapContainer}>
								<button
									className={
										domesticOverseasSwapContent
											? styles.seeDomesticChosen
											: styles.seeDomesticUnchosen
									}
									onClick={() => setDomesticOverseasSwapContent((prev) => !prev)}>
									국내
								</button>
								<button
									className={
										domesticOverseasSwapContent
											? styles.seeOverseasUnchosen
											: styles.seeOverseasChosen
									}
									onClick={() => setDomesticOverseasSwapContent((prev) => !prev)}>
									해외
								</button>
							</div>
							{domesticOverseasSwapContent && (
								<>
									<div className={styles.applyTaxContainer}>
										<div className={styles.applyTaxLabel}>세금 적용 여부</div>
										<div className={styles.applyTaxContent}>
											{dataForRender?.domesticFormData?.isSalaryTaxIncluded
												? '세후'
												: '세전'}
										</div>
									</div>
									<div className={styles.payPeriodContainer}>
										<div className={styles.payPeriodLabel}>임금 지급 주기</div>
										<div className={styles.payPeriodContent}>
											{dataForRender?.domesticFormData?.payrollCycles ===
											'weekly'
												? '주급'
												: '일시불제'}
										</div>
									</div>
									<div className={styles.cutPassContainer}>
										<div className={styles.cutPassLabel}>컷통과</div>
										<div className={styles.cutPassContent}>
											{formatNumber(
												dataForRender.domesticFormData
													?.weeklySalaryCutThrough,
											)}{' '}
											원
										</div>
									</div>
									<div className={styles.cutOutContainer}>
										<div className={styles.cutOutLabel}>컷탈락</div>
										<div className={styles.cutOutContent}>
											{formatNumber(
												dataForRender.domesticFormData?.weeklySalaryCutFail,
											)}{' '}
											원
										</div>
									</div>
									<div className={styles.incentiveOptionContainer}>
										<div className={styles.incentiveOptionLabel}>
											{INCENTIVE_BASIS_LABEL}
										</div>
										<div className={styles.incentiveOptionContent}>
											{formatPrizeTypeLabel(
												dataForRender.domesticFormData?.prizeType,
											)}
										</div>
									</div>
									<div className={styles.incentiveSettingsContainer}>
										<div className={styles.incentiveSettingsLabel}>
											인센티브 금액
										</div>
										<div className={styles.incentiveSettingsContent}>
											{sortIncentiveSettings(
												dataForRender.domesticFormData?.incentives,
											).map((setting) => {
												if (
													setting.percentage === null &&
													setting.endRank === null
												)
													return null
												const displayRank =
													setting.endRank === 1
														? '우승 : '
														: setting.startRank +
															(setting.endRank !== null
																? setting.endRank === 0
																	? ' ~ CUT :  '
																	: ` ~ ${setting.endRank} :  `
																: '~ ')
												const displayPercentage =
													setting.percentage !== null
														? `  ${setting.percentage}%`
														: '미입력'
												return (
													<div
														key={setting.id}
														className={styles.incentiveDisplayItem}>
														<div>{displayRank}</div>
														<div>{displayPercentage}</div>
													</div>
												)
											})}
										</div>
									</div>
									<div className={styles.wearSponserHatContainer}>
										<div className={styles.wearSponserHatLabel}>
											스폰서 모자 착용 여부
										</div>
										<div className={styles.wearSponserHatContent}>
											{dataForRender.domesticFormData?.wearSponsoredHat
												? '착용'
												: '미착용'}
										</div>
									</div>
								</>
							)}
							{!domesticOverseasSwapContent && (
								<>
									<div className={styles.applyTaxContainer}>
										<div className={styles.applyTaxLabel}>세금 적용 여부</div>
										<div className={styles.applyTaxContent}>
											{dataForRender.overseasFormData?.isSalaryTaxIncluded
												? '세후'
												: '세전'}
										</div>
									</div>
									<div className={styles.payPeriodContainer}>
										<div className={styles.payPeriodLabel}>임금 지급 주기</div>
										<div className={styles.payPeriodContent}>
											{dataForRender.overseasFormData?.payrollCycles ===
											'weekly'
												? '주급'
												: '일시불제'}
										</div>
									</div>
									<div className={styles.cutPassContainer}>
										<div className={styles.cutPassLabel}>컷통과</div>
										<div className={styles.cutPassContent}>
											{formatNumber(
												dataForRender.overseasFormData
													?.weeklySalaryCutThrough,
											)}{' '}
											원
										</div>
									</div>
									<div className={styles.cutOutContainer}>
										<div className={styles.cutOutLabel}>컷탈락</div>
										<div className={styles.cutOutContent}>
											{formatNumber(
												dataForRender.overseasFormData?.weeklySalaryCutFail,
											)}{' '}
											원
										</div>
									</div>
									<div className={styles.incentiveOptionContainer}>
										<div className={styles.incentiveOptionLabel}>
											{INCENTIVE_BASIS_LABEL}
										</div>
										<div className={styles.incentiveOptionContent}>
											{formatPrizeTypeLabel(
												dataForRender.overseasFormData?.prizeType,
											)}
										</div>
									</div>
									<div className={styles.incentiveSettingsContainer}>
										<div className={styles.incentiveSettingsLabel}>
											인센티브 금액
										</div>
										<div className={styles.incentiveSettingsContent}>
											{sortIncentiveSettings(
												dataForRender.overseasFormData?.incentives,
											).map((setting) => {
												if (
													setting.percentage === null &&
													setting.endRank === null
												)
													return null
												const displayRank =
													setting.endRank === 1
														? '우승 : '
														: setting.startRank +
															(setting.endRank !== null
																? setting.endRank === 0
																	? ' ~ CUT :  '
																	: ` ~ ${setting.endRank} :  `
																: '~ ')
												const displayPercentage =
													setting.percentage !== null
														? `  ${setting.percentage}%`
														: '미입력'
												return (
													<div
														key={setting.id}
														className={styles.incentiveDisplayItem}>
														<div>{displayRank}</div>
														<div>{displayPercentage}</div>
													</div>
												)
											})}
										</div>
									</div>
									<div className={styles.wearSponserHatContainer}>
										<div className={styles.wearSponserHatLabel}>
											스폰서 모자 착용 여부
										</div>
										<div className={styles.wearSponserHatContent}>
											{dataForRender.overseasFormData?.wearSponsoredHat
												? '착용'
												: '미착용'}
										</div>
									</div>
								</>
							)}
						</>
					)}

				{dataForRender.isOverseas &&
					!dataForRender.isGeneralContract &&
					dataForRender.overseasFormData &&
					!(dataForRender.isDomestic && dataForRender.isOverseas) && (
						<>
							<div className={styles.applyTaxContainer}>
								<div className={styles.applyTaxLabel}>세금 적용 여부</div>
								<div className={styles.applyTaxContent}>
									{dataForRender.overseasFormData?.isSalaryTaxIncluded
										? '세후'
										: '세전'}
								</div>
							</div>
							<div className={styles.payPeriodContainer}>
								<div className={styles.payPeriodLabel}>임금 지급 주기</div>
								<div className={styles.payPeriodContent}>
									{dataForRender.overseasFormData?.payrollCycles === 'weekly'
										? '주급'
										: '일시불제'}
								</div>
							</div>
							<div className={styles.cutPassContainer}>
								<div className={styles.cutPassLabel}>컷통과</div>
								<div className={styles.cutPassContent}>
									{formatNumber(
										dataForRender.overseasFormData?.weeklySalaryCutThrough,
									)}{' '}
									원
								</div>
							</div>
							<div className={styles.cutOutContainer}>
								<div className={styles.cutOutLabel}>컷탈락</div>
								<div className={styles.cutOutContent}>
									{formatNumber(
										dataForRender.overseasFormData?.weeklySalaryCutFail,
									)}{' '}
									원
								</div>
							</div>
							<div className={styles.incentiveOptionContainer}>
								<div className={styles.incentiveOptionLabel}>
									{INCENTIVE_BASIS_LABEL}
								</div>
								<div className={styles.incentiveOptionContent}>
									{formatPrizeTypeLabel(
										dataForRender.overseasFormData?.prizeType,
									)}
								</div>
							</div>
							<div className={styles.incentiveSettingsContainer}>
								<div className={styles.incentiveSettingsLabel}>인센티브 금액</div>
								<div className={styles.incentiveSettingsContent}>
									{dataForRender.overseasFormData?.incentives.map((setting) => {
										if (setting.percentage === null && setting.endRank === null)
											return null
										const displayRank =
											setting.endRank === 1
												? '우승 : '
												: setting.startRank +
													(setting.endRank !== null
														? setting.endRank === 0
															? ' ~ CUT :  '
															: ` ~ ${setting.endRank} :  `
														: '~ ')
										const displayPercentage =
											setting.percentage !== null
												? `  ${setting.percentage}%`
												: '미입력'
										return (
											<div
												key={setting.id}
												className={styles.incentiveDisplayItem}>
												<div>{displayRank}</div>
												<div>{displayPercentage}</div>
											</div>
										)
									})}
								</div>
							</div>
							<div className={styles.wearSponserHatContainer}>
								<div className={styles.wearSponserHatLabel}>
									스폰서 모자 착용 여부
								</div>
								<div className={styles.wearSponserHatContent}>
									{dataForRender.overseasFormData?.wearSponsoredHat
										? '착용'
										: '미착용'}
								</div>
							</div>
						</>
					)}
				{dataForRender.isDomestic &&
					dataForRender.domesticFormData &&
					!(dataForRender.isOverseas && dataForRender.isDomestic) && (
						<>
							<div className={styles.applyTaxContainer}>
								<div className={styles.applyTaxLabel}>세금 적용 여부</div>
								<div className={styles.applyTaxContent}>
									{dataForRender.domesticFormData?.isSalaryTaxIncluded
										? '세후'
										: '세전'}
								</div>
							</div>
							<div className={styles.payPeriodContainer}>
								<div className={styles.payPeriodLabel}>임금 지급 주기</div>
								<div className={styles.payPeriodContent}>
									{dataForRender.domesticFormData?.payrollCycles === 'weekly'
										? '주급'
										: '일시불제'}
								</div>
							</div>
							<div className={styles.cutPassContainer}>
								<div className={styles.cutPassLabel}>컷통과</div>
								<div className={styles.cutPassContent}>
									{formatNumber(
										dataForRender.domesticFormData?.weeklySalaryCutThrough,
									)}{' '}
									원
								</div>
							</div>
							<div className={styles.cutOutContainer}>
								<div className={styles.cutOutLabel}>컷탈락</div>
								<div className={styles.cutOutContent}>
									{formatNumber(
										dataForRender.domesticFormData?.weeklySalaryCutFail,
									)}{' '}
									원
								</div>
							</div>
							<div className={styles.incentiveOptionContainer}>
								<div className={styles.incentiveOptionLabel}>
									{INCENTIVE_BASIS_LABEL}
								</div>
								<div className={styles.incentiveOptionContent}>
									{formatPrizeTypeLabel(
										dataForRender.domesticFormData?.prizeType,
									)}
								</div>
							</div>
							<div className={styles.incentiveSettingsContainer}>
								<div className={styles.incentiveSettingsLabel}>인센티브 금액</div>
								<div className={styles.incentiveSettingsContent}>
									{sortIncentiveSettings(
										dataForRender.domesticFormData?.incentives,
									).map((setting) => {
										if (setting.percentage === null && setting.endRank === null)
											return null
										const displayRank =
											setting.endRank === 1
												? '우승 : '
												: setting.startRank +
													(setting.endRank !== null
														? setting.endRank === 0
															? ' ~ CUT :  '
															: ` ~ ${setting.endRank} :  `
														: '~ ')
										const displayPercentage =
											setting.percentage !== null
												? `  ${setting.percentage}%`
												: '미입력'
										return (
											<div
												key={setting.id}
												className={styles.incentiveDisplayItem}>
												<div>{displayRank}</div>
												<div>{displayPercentage}</div>
											</div>
										)
									})}
								</div>
							</div>
							<div className={styles.wearSponserHatContainer}>
								<div className={styles.wearSponserHatLabel}>
									스폰서 모자 착용 여부
								</div>
								<div className={styles.wearSponserHatContent}>
									{dataForRender.domesticFormData?.wearSponsoredHat
										? '착용'
										: '미착용'}
								</div>
							</div>
						</>
					)}
				{/* <div className={styles.authorHeader}>작성자 정보</div>
				<div className={styles.author}>
					{authStatus.user.name + ' / ' + dataForRender.userAssociation}
				</div> */}
				<div className={styles.agreement}>
					<div className={styles.agreementLeftContainer}>
						<div className={styles.mainAgreementContent}>
							<span className={styles.mainAgreementContentTitle}>
								스포이음 이용약관에 따라 수수로가 부과됩니다.
							</span>
						</div>
					</div>
					<CheckCircleIcon
						className={`${styles.checkIcon} ${firstLevelAgreeChecked ? styles.checkedIcon : ''}`}
						onClick={() => setFirstLevelAgreeChecked(!firstLevelAgreeChecked)}
					/>
				</div>
				<div className={styles.agreementSecond}>
					<div className={styles.agreementLeftContainer}>
						<div className={styles.mainAgreementContent}>
							<span className={styles.mainAgreementContentTitle}>
								계약 체결이 완료되면, 같은 기간에 다른 제안서들은 자동으로
								거절됩니다.
							</span>
						</div>
					</div>
					<CheckCircleIcon
						className={`${styles.checkIcon} ${secondLevelAgreeChecked ? styles.checkedIcon : ''}`}
						onClick={() => setSecondLevelAgreeChecked(!secondLevelAgreeChecked)}
					/>
				</div>
				<div className={styles.shareCall}>{`[계약이 성사되면 연락처가 공유돼요]`}</div>
			</div>
			<div className={styles.activateButtonContainer}>
				<button
					className={`${styles.activateButton} ${nextButtonActivated ? styles.activated : ''} ${
						createContractMutation.isPending ? styles.loading : ''
					}`}
					onClick={() => handleOffer()}
					disabled={createContractMutation.isPending || !nextButtonActivated}>
					{createContractMutation.isPending ? (
						<div className={styles.loadingSpinner}></div>
					) : (
						'계약 제안하기'
					)}
				</button>
				<button
					className={styles.closeButton}
					onClick={() => {
						onClose()
					}}>
					닫기
				</button>
			</div>
		</div>
	)
})

export default OfferModal
